import type { TareLocalPrefs } from "../types/TareBridge";

import { getTareBridge } from "./getTareBridge";
import { callPromiseSafely } from "../../util/callPromiseSafely";

/**
 * Local preferences functionality wrapper that uses TareBridge methods directly.
 */
export const localPrefs: TareLocalPrefs = {
  async getBoolean(namespace: string, key: string, defaultValue?: boolean): Promise<boolean> {
    const bridge = getTareBridge();
    if (!bridge?.getLocalPrefsBoolean) {
      return defaultValue ?? false;
    }
    const result = await callPromiseSafely(() =>
      bridge.getLocalPrefsBoolean!(namespace, key, defaultValue),
    );
    return result ?? defaultValue ?? false;
  },

  async setBoolean(namespace: string, key: string, newValue: boolean): Promise<void> {
    const bridge = getTareBridge();
    if (!bridge?.setLocalPrefsBoolean) {
      return;
    }
    await callPromiseSafely(() => bridge.setLocalPrefsBoolean!(namespace, key, newValue));
  },

  async getInt(namespace: string, key: string, defaultValue?: number): Promise<number> {
    const bridge = getTareBridge();
    if (!bridge?.getLocalPrefsInt) {
      return defaultValue ?? 0;
    }
    const result = await callPromiseSafely(() =>
      bridge.getLocalPrefsInt!(namespace, key, defaultValue),
    );
    return result ?? defaultValue ?? 0;
  },

  async setInt(namespace: string, key: string, newValue: number): Promise<void> {
    const bridge = getTareBridge();
    if (!bridge?.setLocalPrefsInt) {
      return;
    }
    await callPromiseSafely(() => bridge.setLocalPrefsInt!(namespace, key, newValue));
  },

  async getLong(namespace: string, key: string, defaultValue?: number): Promise<number> {
    const bridge = getTareBridge();
    if (!bridge?.getLocalPrefsLong) {
      return defaultValue ?? 0;
    }
    const result = await callPromiseSafely(() =>
      bridge.getLocalPrefsLong!(namespace, key, defaultValue),
    );
    return result ?? defaultValue ?? 0;
  },

  async setLong(namespace: string, key: string, newValue: number): Promise<void> {
    const bridge = getTareBridge();
    if (!bridge?.setLocalPrefsLong) {
      return;
    }
    await callPromiseSafely(() => bridge.setLocalPrefsLong!(namespace, key, newValue));
  },

  async getString(namespace: string, key: string, defaultValue?: string): Promise<string> {
    const bridge = getTareBridge();
    if (!bridge?.getLocalPrefsString) {
      return defaultValue ?? "";
    }
    const result = await callPromiseSafely(() =>
      bridge.getLocalPrefsString!(namespace, key, defaultValue),
    );
    return result ?? defaultValue ?? "";
  },

  async setString(namespace: string, key: string, newValue: string): Promise<void> {
    const bridge = getTareBridge();
    if (!bridge?.setLocalPrefsString) {
      return;
    }
    await callPromiseSafely(() => bridge.setLocalPrefsString!(namespace, key, newValue));
  },

  async clearNamespace(namespace: string): Promise<void> {
    const bridge = getTareBridge();
    if (!bridge?.clearLocalPrefsNamespace) {
      return;
    }
    await callPromiseSafely(() => bridge.clearLocalPrefsNamespace!(namespace));
  },
};
